import { useMemo } from 'react';
import IcWarning from '@/assets/icons/ic-warning-circle.svg';
import IcHidden from '@/assets/icons/ic-hidden.svg';
import { cn } from '@/lib';
import { PageStatus } from '@/types/store';
import { findRoutingCycles, getBrokenRoutes, getFunnelPageRelationships, getReachablePageIds } from '../../../logic';
import type { FunnelPageItem } from '../../../types';

interface FunnelSummaryCardProps {
    pages: FunnelPageItem[];
}

function useSummaryData(pages: FunnelPageItem[]) {
    const visiblePages = useMemo(() => pages.filter(p => p.status !== PageStatus.DRAFT), [pages]);
    const hiddenPages = useMemo(() => pages.filter(p => p.status === PageStatus.DRAFT), [pages]);

    const relationships = useMemo(() => getFunnelPageRelationships(visiblePages), [visiblePages]);

    const warningCount = useMemo(() => {
        const cycles = findRoutingCycles(visiblePages).length;
        const broken = getBrokenRoutes(visiblePages).length;
        const reachable = getReachablePageIds(visiblePages);
        const orphans = visiblePages.filter(p => !reachable.has(p.page_id)).length;
        return cycles + broken + orphans;
    }, [visiblePages]);

    const upsellCount = [...relationships.values()].filter(r => r.kind === 'upsell').length;
    const downsellCount = [...relationships.values()].filter(r => r.kind === 'downsell').length;

    return {
        pageCount: visiblePages.length,
        upsellCount,
        downsellCount,
        warningCount,
        hiddenCount: hiddenPages.length,
    };
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <p className='text-caption-2-bold text-gray tracking-[0.08em] uppercase'>{children}</p>
);

const WarningChip = ({ count }: { count: number }) => {
    if (count <= 0) return null;
    return (
        <div className='bg-warning-subtle flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5'>
            <IcWarning className='text-warning size-3' />
            <span className='text-caption-2-bold text-warning'>
                {count} {count === 1 ? 'issue' : 'issues'}
            </span>
        </div>
    );
};

const Stat = ({ value, label, tone }: { value: number; label: string; tone: 'dark' | 'success' | 'warning' }) => (
    <div className='flex flex-col'>
        <span
            className={cn(
                'text-heading-h4',
                tone === 'dark' && 'text-dark',
                tone === 'success' && 'text-success',
                tone === 'warning' && 'text-warning'
            )}
        >
            {value}
        </span>
        <span className='text-caption-2-regular text-gray'>{label}</span>
    </div>
);

export const FunnelSummaryCard = ({ pages }: FunnelSummaryCardProps) => {
    const { pageCount, upsellCount, downsellCount, warningCount, hiddenCount } = useSummaryData(pages);

    if (pageCount === 0 && hiddenCount === 0) return null;

    return (
        <div className='bg-canvas border-neutral-subtle shadow-stan rounded-2xl border p-4'>
            <div className='flex items-center justify-between gap-2'>
                <SectionLabel>Summary</SectionLabel>
                <WarningChip count={warningCount} />
            </div>

            <div className='mt-3 flex items-center gap-4'>
                <Stat value={pageCount} label={pageCount === 1 ? 'Page' : 'Pages'} tone='dark' />
                <div className='bg-neutral-subtle h-6 w-px' />
                <Stat value={upsellCount} label={upsellCount === 1 ? 'Upsell' : 'Upsells'} tone='success' />
                <div className='bg-neutral-subtle h-6 w-px' />
                <Stat value={downsellCount} label={downsellCount === 1 ? 'Downsell' : 'Downsells'} tone='warning' />
            </div>

            {hiddenCount > 0 && (
                <div className='border-neutral-subtle mt-3 flex items-center gap-1.5 border-t pt-3'>
                    <IcHidden className='text-gray size-3.5' />
                    <span className='text-caption-2-regular text-gray'>
                        {hiddenCount} hidden {hiddenCount === 1 ? 'page' : 'pages'}
                    </span>
                </div>
            )}
        </div>
    );
};
